/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect, useState, useCallback } from 'react';

import clsx from 'clsx';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import ErrorMessage from 'apps/web_forms/components/commons/ErrorMessage';
import useChangeValue from 'apps/web_forms/hooks/useChangeValue';
import useResourceUpdateHighlight from 'apps/web_forms/hooks/useResourceUpdateHighlight';
import { selectFieldErrorById } from 'apps/web_forms/hooks/useGlobalSelector';
import useRules from 'apps/web_forms/hooks/useRules';
import { setCurrentSelectedMultimediaInput } from 'apps/web_forms/slices/multimediaSlice';
import { COLORS, STYLES } from 'apps/web_forms/utils/constants';
import customPropTypes from 'apps/web_forms/utils/customPropTypes';

const StyledBooleanInputContainer = styled.div`
  display: flex;
  flex-direction: column;
  .boolean-container {
    display: flex;
    label {
      display: flex;
      align-items: center;
      margin-right: 0.4em;
    }

    @supports (-webkit-appearance: none) or (-moz-appearance: none) {
      input[type='radio'] {
        -webkit-appearance: none;
        -moz-appearance: none;
        border: 1px solid ${COLORS.borderBooleanRadio};
        outline: none;
        transition: box-shadow 0.2s;
        border-radius: 50%;
        width: 1.25em;
        height: 1.25em;
        margin-right: 0.4em;
        background-color: ${COLORS.booleanInputBackgroundColor};
        cursor: default;
        position: relative;
        top: -0.1em;

        &:checked {
          background-color: ${COLORS.green};
        }
        &:required:invalid {
          border: ${STYLES.ErrorHighlight};
        }
      }

      input[readonly].read-only {
        background-color: ${COLORS.readOnlyBooleanInputBackgroundColor};

        &:checked {
          background-color: ${COLORS.darkGreen};
        }
      }
    }
  }
`;

const BooleanInput = ({ element, formContext, onChangeValue, toggleActiveValue, showContent }) => {
  const { title, identifier, hint, hidden, validate_msg: validateMsg } = element;
  const { register, getValues } = formContext;

  const { changeValue, currentValue } = useChangeValue(
    element,
    onChangeValue,
    formContext
  );

  const errorMessage = useSelector((state) => selectFieldErrorById(state, identifier));
  
  const { hasResourceUpdateHighlight, removeResourceUpdateHighlight } =
    useResourceUpdateHighlight({ fieldIdentifier: identifier });

  const dispatch = useDispatch();
  const [{ isActive, isElementValid, isRequired, isReadOnly, initialAnswer }] =
    useRules(element, formContext);

  const [activeValue, setActiveValue] = useState({
    rbTrue: '',
    rbFalse: '',
  });

  useEffect(() => {
    register(identifier, {
      validate: () => isElementValid || validateMsg || false,
      required: isRequired ? 'This field is required' : false,
    });
  }, [identifier, isElementValid, validateMsg, isRequired]);

  useEffect(() => {
    let answer;
  
    let virtualValue = getValues(identifier);
  
    if (['true', '1', true].includes(initialAnswer || virtualValue)) answer = 'true';
    if (['false', '0', false].includes(initialAnswer || virtualValue)) answer = 'false';
  
    if (answer) {
      setActiveValue({
        rbTrue: answer === 'true',
        rbFalse: answer === 'false',
      });
  
      if (answer !== currentValue) {
        changeValue(answer);
      }
    } else {
      changeValue('');
    }
  }, [initialAnswer, identifier, getValues, changeValue, currentValue]);

  const toggleRadiobutton = useCallback((e) => {
    let value = '';
    let newActiveValue = { ...activeValue };
  
    if (e.target.value === 'true') {
      newActiveValue = { rbFalse: false, rbTrue: !activeValue.rbTrue };
      value = !activeValue.rbTrue && 'true';
    } else if (e.target.value === 'false') {
      newActiveValue = { rbFalse: !activeValue.rbFalse, rbTrue: false };
      value = !activeValue.rbFalse && 'false';
    } else {
      newActiveValue = { rbFalse: '', rbTrue: '' };
    }
  
    setActiveValue(newActiveValue);
    dispatch(setCurrentSelectedMultimediaInput({}));
    changeValue(typeof value === "boolean" ? '' : value, true);
  }, [activeValue, changeValue, dispatch]);
  

  useEffect(() => {
    toggleActiveValue(identifier, isActive);
  }, [isActive]);

  if (!isActive || hidden || !showContent) return null;

  const commonProps = {
    className: clsx(
      errorMessage !== undefined && 'invalid',
      hasResourceUpdateHighlight && 'updateHighlight',
      isReadOnly && 'read-only'
    ),
    type: 'radio',
    disabled: isReadOnly,
    name: identifier,
    onChange: toggleRadiobutton,
    onFocus: removeResourceUpdateHighlight
  };

  return (
    <div className='react-form-control'>
      <StyledBooleanInputContainer>
        <label htmlFor={`rbTrue-${identifier}`}>{title}</label>
        <StyledBooleanInputContainer readOnly={isReadOnly}>
          <div className='boolean-container'>
            <label htmlFor={`rbTrue-${identifier}`} className='boolean-label'>

              <input
                {...commonProps}
                id={`rbTrue-${identifier}`}
                value='true'
                checked={activeValue.rbTrue}
              />
              Yes
            </label>
            <label htmlFor={`rbFalse-${identifier}`} className='boolean-label'>
              <input
                {...commonProps}
                id={`rbFalse-${identifier}`}
                value='false'
                checked={activeValue.rbFalse}
              />
              No
            </label>
          </div>
        </StyledBooleanInputContainer>
        {hint && <p className='element-hint'>{hint}</p>}
        {errorMessage !== undefined && (
          <ErrorMessage id={`${identifier}_errmsg`}>
            {errorMessage}
          </ErrorMessage>
        )}
      </StyledBooleanInputContainer>
    </div>
  );
};

BooleanInput.propTypes = {
  element: PropTypes.shape({
    title: PropTypes.string,
    identifier: PropTypes.string,
    initialAnswer: PropTypes.string,
    hint: PropTypes.string,
    hidden: PropTypes.bool,
    validate_msg: PropTypes.string,
  }).isRequired,
  formContext: customPropTypes.formContext.isRequired,
  onChangeValue: PropTypes.func.isRequired,
};

export default React.memo(BooleanInput);
